import React from 'react';
import { useTranslation } from 'react-i18next';

interface Solution {
  id: string;
  title: string;
  description: string;
  icon: string;
  features: string[];
}

interface SolutionsProps {
  solutions: Solution[];
}

const Solutions: React.FC<SolutionsProps> = ({ solutions }) => {
  const { t } = useTranslation();

  return (
    <section id="solutions" className="section-padding relative overflow-hidden">
      {/* Decorative background elements */}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-accent-green/30 to-transparent"></div>
      <div className="absolute top-20 right-0 w-96 h-96 bg-accent-green/5 rounded-full blur-3xl"></div>
      <div className="absolute bottom-20 left-0 w-64 h-64 bg-accent-green/5 rounded-full blur-3xl"></div>
      
      <div className="container-custom relative z-10">
        {/* Section header and CTA - Side by side */}
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center mb-8 sm:mb-12">
          {/* Left: Section header */}
          <div className="text-center lg:text-left">
            <div className="inline-flex flex-col items-center lg:items-start">
              <span className="text-accent-green text-sm font-semibold tracking-wider uppercase mb-3">
                Naše riešenia
              </span>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
                {t('solutions.title')}
              </h2>
              <div className="w-20 h-1 bg-accent-green rounded-full mb-6 lg:mx-0"></div>
              <p className="text-base sm:text-xl text-gray-300 max-w-xl leading-relaxed">
                {t('solutions.description')}
              </p>
            </div>
          </div>

          {/* Right: CTA box */}
          <div className="flex justify-center lg:justify-end">
            <div className="inline-flex flex-col sm:flex-row items-center gap-4 sm:gap-6 bg-tech-dark rounded-2xl px-8 py-5 border border-accent-green/50 shadow-lg shadow-accent-green/10 hover:border-accent-green transition-all duration-300">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-accent-green/20 flex items-center justify-center">
                  <span className="text-accent-green text-lg">💡</span>
                </div>
                <span className="text-white text-sm sm:text-base font-medium text-center sm:text-left">
                  {t('solutions.ctaText')}
                </span>
              </div>
              <button className="btn-primary text-sm whitespace-nowrap !px-6 !py-2.5">
                {t('solutions.ctaBtn')}
              </button>
            </div>
          </div>
        </div>

        {/* Technology Partners */}
        <div className="mt-12 pt-8 border-t border-[#2a2a2a]">
          <div className="text-center mb-6">
            <h3 className="text-3xl md:text-4xl font-bold text-white">
              Technologickí partneri
            </h3>
          </div>
          <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
            {/* Sinexcel */}
            <div className="bg-tech-dark rounded-xl p-6 border border-[#2a2a2a] hover:border-accent-green/50 transition-all duration-300">
              <div className="flex items-center gap-4 mb-3">
                <div className="w-24 h-12 flex items-center justify-center bg-white rounded-lg p-2">
                  <img src="/Sinexcel.png" alt="Sinexcel logo" className="max-w-full max-h-full object-contain" />
                </div>
                <div>
                  <h4 className="text-white font-semibold">Sinexcel</h4>
                  <span className="text-accent-green text-xs">Storage Inverters</span>
                </div>
              </div>
              <p className="text-gray-400 text-sm">
                Zástupca značky Sinexcel- meničov pre Slovensko. Pokročilé riešenia pre ukladanie energie.
              </p>
            </div>

            {/* Sunwoda */}
            <div className="bg-tech-dark rounded-xl p-6 border border-[#2a2a2a] hover:border-accent-green/50 transition-all duration-300">
              <div className="flex items-center gap-4 mb-3">
                <div className="w-24 h-12 flex items-center justify-center bg-white rounded-lg p-2">
                  <img src="/Sunvoda.webp" alt="Sunwoda logo" className="max-w-full max-h-full object-contain" />
                </div>
                <div>
                  <h4 className="text-white font-semibold">Sunwoda</h4>
                  <span className="text-accent-green text-xs">Batériové systémy</span>
                </div>
              </div>
              <p className="text-gray-400 text-sm">
                Zástupca batériových systémov Sunwoda pre Slovensko. Spoľahlivé riešenia pre energetickú nezávislosť.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Solutions;
